import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import { useGetPrograms, useDeleteProgram } from "../../api/hooks/useWorkout";
import { showError, showSuccess } from "../../helpers/toast";
import Pagination from "../../components/Panigation";
import { DIFFICULTY_LEVEL } from "../../constants/settings";
import ConfirmationDialog from "../../components/ConfirmationDialog";

const PROGRAM_PAGE_LIMIT_OPTIONS = [10, 25, 50, 100] as const;

const ManageProgram = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<string | null>(null);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timerId);
  }, [searchTerm]);

  const { data: programsData, isLoading, error: programsError, refetch } = useGetPrograms({
    search: debouncedSearchTerm || undefined,
    page,
    limit,
    offset: (page - 1) * limit,
  });

  const { programs, totalCount } = useMemo(() => {
    const raw = programsData?.data;
    let list: any[] = [];
    let total = 0;
    if (Array.isArray(raw)) {
      list = raw;
      total = raw.length;
    } else if (raw && typeof raw === "object") {
      if (Array.isArray((raw as any).list)) {
        list = (raw as any).list;
        total = (raw as any).totalCount || list.length;
      }
    }
    return {
      programs: Array.isArray(list) ? list : [],
      totalCount: total || 0,
    };
  }, [programsData]);

  useEffect(() => {
    if (programsError) {
      showError((programsError as any)?.response?.data?.message || "Failed to load programs");
    }
  }, [programsError]);

  const formatDifficulty = (val?: number | number[]) => {
    const toCodes = (v: any) => {
      if (Array.isArray(v)) return v.map((x) => Number(x)).filter(Number.isFinite);
      const n = Number(v);
      return Number.isFinite(n) ? [n] : [];
    };

    const codes = toCodes(val);
    if (!codes.length) return "N/A";

    const labels = codes
      .map((code) => {
        if (code === DIFFICULTY_LEVEL.BEGGINER) return "Beginner";
        if (code === DIFFICULTY_LEVEL.BEGINNER_INTERMEDIATE) return "Beginner-Intermediate";
        if (code === DIFFICULTY_LEVEL.INTERMEDIATE) return "Intermediate";
        if (code === DIFFICULTY_LEVEL.ADVANCED) return "Advanced";
        return null;
      })
      .filter(Boolean) as string[];

    return labels.length ? labels.join(", ") : "N/A";
  };

  // Handle delete
  const { mutate: deleteProgram, isPending: isDeleting } = useDeleteProgram({
    onSuccess: (response) => {
      showSuccess(response.message || "Program deleted successfully");
      refetch();
      setDeleteDialogOpen(false);
      setProgramToDelete(null);
    },
    onError: (error: any) => {
      showError(error?.response?.data?.message || "Failed to delete program");
    },
  });

  const handleDelete = (id: string) => {
    setProgramToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (programToDelete) {
      deleteProgram(programToDelete);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setProgramToDelete(null);
  };

  const handleLimitChange = (event: SelectChangeEvent<number>) => {
    const value = Number(event.target.value);
    if (!Number.isFinite(value) || value < 1) return;
    setLimit(value);
    setPage(1);
  };

  return (
    <>
      <div className="main_title">
        <h1>Manage Programs</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Manage Programs
        </p>
      </div>

      <div className="cards">
        {/* Header with Search and Add Button */}
        <div className="table_header">
          <div className="left_s">
            <SearchBar
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
              onCross={() => setSearchTerm("")}
              searchTerm={searchTerm}
              setDebouncedSearchTerm={setSearchTerm}
              placeholder="Search Programs"
            />
          </div>

          <div className="right_s" style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="program-list-limit-label">Per page</InputLabel>
              <Select<number>
                labelId="program-list-limit-label"
                id="program-list-limit"
                label="Per page"
                value={limit}
                onChange={handleLimitChange}
              >
                {PROGRAM_PAGE_LIMIT_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button className="btn btn_primary" onClick={() => navigate("add")}>
              <AddIcon /> Add Program
            </Button>
          </div>
        </div>

        {/* Table */}
        <TableContainer className="table_container">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>Program Details</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Difficulty</TableCell>
                <TableCell>Enrolled Users</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : programs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <p>No programs found</p>
                  </TableCell>
                </TableRow>
              ) : (
                programs.map((program: any, index: number) => (
                  <TableRow hover key={program._id}>
                    {/* S.No */}
                    <TableCell>{index + 1}</TableCell>

                    {/* Program Details */}
                    <TableCell>
                      <div className="user_block">
                        <figure>
                          <img
                            src={program.thumnail || "/static/images/program_placeholder.png"}
                            alt={program.title || "Program"}
                            style={{ objectFit: "contain" }}
                          />
                        </figure>
                        <p>
                          <b>{program.title || "N/A"}</b>
                          {program.subTitle && <span>{program.subTitle}</span>}
                          {program.uniqueId && <span style={{ fontSize: "12px", color: "#6c757d" }}>ID: {program.uniqueId}</span>}
                        </p>
                      </div>
                    </TableCell>

                    {/* Duration */}
                    <TableCell>
                      {program.durationInWeeks
                        ? `${program.durationInWeeks} ${Number(program.durationInWeeks) === 1
                          ? "Week"
                          : "Weeks"
                        }`
                        : "N/A"}
                    </TableCell>

                    {/* Difficulty */}
                    <TableCell>{formatDifficulty(program.difficultyLevel)}</TableCell>

                    {/* Enrolled Users */}
                    <TableCell>{program.enrolledUsers || 0}</TableCell>

                    {/* Actions */}
                    <TableCell>
                      <div className="table_actions v2">
                        <IconButton
                          onClick={() => navigate("view", { state: { programId: program._id } })}
                        >
                          <img
                            src="/static/images/eye_icon.svg"
                            alt="View"
                          />
                        </IconButton>
                        <IconButton
                          onClick={() =>
                            navigate(`edit?programId=${encodeURIComponent(program._id)}`)
                          }
                        >
                          <img src="/static/images/edit2_icon.svg" alt="Edit" />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(program._id)}
                          disabled={isDeleting}
                        >
                          <img
                            src="/static/images/trash2_icon.svg"
                            alt="Delete"
                          />
                        </IconButton>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {!isLoading && programs.length > 0 && (
          <Pagination
            module={programs}
            page={page}
            onPageChange={(newPage) => {
              setPage(newPage);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            totalPages={Math.ceil(totalCount / limit)}
            totalCount={totalCount}
            setPage={setPage}
            limit={limit}
          />
        )}
      </div>

      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Program"
        message="Are you sure you want to delete this program? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};

export default ManageProgram;
